import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getRoomById, imageUrl, getRoomEquipments } from '../api/rooms';
import ImageCarousel from '../components/ImageCarousel';
import { createOrGetConversation } from '../api/chat';

// --- Composants UI internes ---

const InfoBadge = ({ icon, label, value }) => {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-xl border border-gray-100 text-center min-w-[80px]">
      <span className="text-2xl mb-1">{icon}</span>
      <span className="text-sm font-bold text-gray-900">{value}</span>
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
  );
};

const SidebarRow = ({ label, value, highlight = false }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
    <span className="text-gray-600 text-sm">{label}</span>
    <span className={`font-medium ${highlight ? 'text-green-600' : 'text-gray-900'}`}>
      {value}
    </span>
  </div>
);

const formatDate = (dateString) => {
  if (!dateString) return 'Non spécifié';
  return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function AnnonceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([
      getRoomById(id),
      getRoomEquipments(id).catch(() => [])
    ])
      .then(([roomData, equipmentsData]) => {
        if (!alive) return;

        const imgsRaw = Array.isArray(roomData.imageUrl) ? roomData.imageUrl : (roomData.images || []);
        const imgs = imgsRaw.filter(Boolean).map(imageUrl);

        // CORRECTION DU FILTRE ÉQUIPEMENTS
        // On ne garde que ceux qui ont 'isSelected' ou 'IsSelected' à true
        const finalEquipments = Array.isArray(equipmentsData) 
          ? equipmentsData
              .filter(e => {
                  if (typeof e === 'string') return true; 
                  // Vérifie IsSelected (C# standard) ou isSelected (JS standard)
                  return e.IsSelected === true || e.isSelected === true || e.HasEquipment === true || e.hasEquipment === true;
              })
              .map(e => e.name || e.Name || e.equipmentName || e)
          : [];

        setItem({
          ...roomData,
          title: roomData.title ?? roomData.name ?? 'Hébergement sans nom',
          locationFull: [roomData.location?.street, roomData.location?.district, roomData.location?.city, roomData.location?.country].filter(Boolean).join(', '),
          priceLabel: roomData.price?.amount ? `${roomData.price.amount.toLocaleString('fr-FR')} ${roomData.price.currency || 'XAF'}` : 'Prix sur demande',
          
          equipmentsList: finalEquipments,
          
          maxGuests: roomData.maxGuests ?? 0,
          bedCount: roomData.bedCount ?? 0,
          minRooms: roomData.minRooms ?? 0,
          sizeInSquareMeters: roomData.sizeInSquareMeters ?? 0,
          images: imgs
        });
      })
      .catch((e) => {
        console.error(e);
        if (alive) setError("Impossible de charger l'annonce.");
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false };
  }, [id]);

  const handleContact = async () => {
    try {
      if (!item.hostID) return alert("Erreur: Propriétaire introuvable.");
      const conversation = await createOrGetConversation(item.hostID, item.roomID);
      const chatId = conversation.id || conversation;
      navigate(`/chat/${chatId}`);
    } catch (err) {
      console.error(err);
      alert("Impossible de contacter le propriétaire.");
    }
  };

  if (loading) return <div className="flex justify-center items-center h-96"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (error || !item) return <div className="container mx-auto px-4 py-12 text-center"><h2 className="text-2xl font-bold text-gray-800 mb-4">{error || 'Annonce introuvable'}</h2><Link to="/" className="text-blue-600 hover:underline">Retour à l'accueil</Link></div>;

  return (
    <section className="bg-gray-50 min-h-screen pb-12">
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-sm font-medium text-gray-500 hover:text-blue-600 transition flex items-center gap-1">← Retour</Link>
          <div className="flex gap-2"><button className="text-sm text-gray-600 hover:text-blue-600 px-3 py-1 rounded-md hover:bg-blue-50 transition">Partager 📤</button></div>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-6">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">{item.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span className="flex items-center gap-1">📍 {item.locationFull}</span>
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            {item.isVerified && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold uppercase">Vérifié</span>}
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            <span className="text-blue-600 font-medium">{item.type}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg aspect-video relative">
              <div className="w-full h-full [&_img]:w-full [&_img]:h-full [&_img]:object-cover">
                 <ImageCarousel images={item.images} alt={item.title} />
              </div>
              <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase text-white shadow-md ${item.isActive ? 'bg-green-500' : 'bg-red-500'}`}>
                {item.isActive ? 'Disponible' : 'Indisponible'}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <InfoBadge icon="👥" label="Max Pers." value={item.maxGuests} />
              <InfoBadge icon="🛏️" label="Chambres" value={item.minRooms} />
              <InfoBadge icon="📏" label="Surface" value={item.sizeInSquareMeters ? `${item.sizeInSquareMeters} m²` : null} />
              <InfoBadge icon="🚿" label="Sdb" value={item.hasPrivateBathroom ? 'Privée' : 'Partagée'} />
              <InfoBadge icon="🚪" label="Lits" value={item.bedCount} />
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="text-xl font-bold mb-4 text-gray-900">À propos</h3>
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed text-base">{item.description || "Aucune description."}</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h3 className="text-xl font-bold mb-6 text-gray-900">Ce que propose ce logement</h3>
              {item.equipmentsList && item.equipmentsList.length > 0 ? (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                  {item.equipmentsList.map((eq, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 font-bold text-sm">✓</div>
                      <span className="capitalize">{eq}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-center italic border border-dashed border-gray-200">
                  Les équipements n'ont pas encore été renseignés.
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 sticky top-24 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <span className="text-3xl font-extrabold text-gray-900">{item.priceLabel}</span>
                  <span className="text-gray-500 text-sm"> / {item.price?.unit || 'mois'}</span>
                </div>
                {item.isNegotiable && <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded uppercase">Négociable</span>}
              </div>

              <div className="space-y-4 mb-6">
                <div className="border rounded-xl p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="block text-gray-500 text-xs uppercase">Arrivée (min)</span>
                      <span className="font-semibold">{formatDate(item.availableFrom)}</span>
                    </div>
                    <div>
                      <span className="block text-gray-500 text-xs uppercase">Départ (max)</span>
                      <span className="font-semibold">{formatDate(item.availableTo)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600 px-1">
                  <SidebarRow label="Séjour minimum" value={`${item.minimumStayInNights || 1} nuit(s)`} />
                  <SidebarRow label="Séjour maximum" value={`${item.maximumStayInNights || 365} nuit(s)`} />
                  <SidebarRow label="Frais de ménage" value={item.cleaningFee > 0 ? `${item.cleaningFee} ${item.price?.currency}` : 'Inclus'} />
                </div>
              </div>

              <button onClick={handleContact} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all transform hover:scale-[1.02] shadow-md flex justify-center items-center gap-2">
                <span>💬</span> Discuter avec l'hôte
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}